import { create } from 'zustand';
import { supabase } from '../lib/supabase';

/**
 * Auth store — manages admin authentication state.
 * Persists session via Supabase Auth (local storage).
 */
export const useAuthStore = create((set, get) => ({
  user: null,
  session: null,
  isOwner: false,
  loading: true,
  error: null,
  failedAttempts: 0,
  cooldownUntil: null,
  _initialized: false,

  /**
   * Initialize — idempotent, call from any entry point.
   * Restores session from Supabase local storage.
   */
  initialize: async () => {
    if (get()._initialized) return;
    set({ _initialized: true });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const isOwner = await get()._checkOwner(session.user.email);
        set({ user: session.user, session, isOwner, loading: false });
      } else {
        set({ loading: false });
      }

      // Listen for auth state changes
      supabase.auth.onAuthStateChange(async (_event, session) => {
        if (session) {
          const isOwner = await get()._checkOwner(session.user.email);
          set({ user: session.user, session, isOwner, error: null });
        } else {
          set({ user: null, session: null, isOwner: false });
        }
      });
    } catch {
      set({ loading: false });
    }
  },

  /**
   * Sign in with email + password.
   */
  signIn: async (email, password) => {
    const { cooldownUntil, failedAttempts } = get();

    // Check cooldown
    if (cooldownUntil && Date.now() < cooldownUntil) {
      const secs = Math.ceil((cooldownUntil - Date.now()) / 1000);
      set({ error: `Too many attempts. Try again in ${secs}s.` });
      return { success: false };
    }

    set({ loading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const newAttempts = failedAttempts + 1;
        const cooldown = newAttempts >= 5 ? Date.now() + 60000 : null; // 60s cooldown after 5 fails
        set({
          loading: false,
          error: error.message,
          failedAttempts: newAttempts,
          cooldownUntil: cooldown,
        });
        return { success: false };
      }

      // Check if user is the owner
      const isOwner = await get()._checkOwner(data.user.email);

      if (!isOwner) {
        await supabase.auth.signOut();
        set({
          loading: false,
          user: null,
          session: null,
          isOwner: false,
          error: 'Access denied. This account is not authorized to access the admin panel.',
        });
        return { success: false };
      }

      // Log the admin sign-in
      await supabase.from('admin_activity').insert({
        user_id: data.user.id,
        email: data.user.email,
        action: 'sign_in',
      });

      set({
        user: data.user,
        session: data.session,
        isOwner: true,
        loading: false,
        failedAttempts: 0,
        cooldownUntil: null,
        error: null,
      });

      return { success: true };
    } catch (err) {
      set({ loading: false, error: 'An unexpected error occurred.' });
      return { success: false };
    }
  },

  /**
   * Send password reset email via Supabase magic link.
   */
  resetPassword: async (email) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/admin/reset-password`,
      });
      set({ loading: false });
      if (error) {
        set({ error: error.message });
        return { success: false };
      }
      return { success: true };
    } catch {
      set({ loading: false, error: 'Failed to send reset email.' });
      return { success: false };
    }
  },

  /**
   * Update password (after clicking reset link).
   */
  updatePassword: async (newPassword) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      set({ loading: false });
      if (error) {
        set({ error: error.message });
        return { success: false };
      }
      return { success: true };
    } catch {
      set({ loading: false, error: 'Failed to update password.' });
      return { success: false };
    }
  },

  /**
   * Sign out.
   */
  signOut: async () => {
    await supabase.auth.signOut();
    set({
      user: null,
      session: null,
      isOwner: false,
      error: null,
      failedAttempts: 0,
      cooldownUntil: null,
    });
  },

  clearError: () => set({ error: null }),

  /**
   * Internal — check if email matches the owner in admin_config.
   */
  _checkOwner: async (email) => {
    try {
      const { data } = await supabase
        .from('admin_config')
        .select('owner_email')
        .eq('owner_email', email)
        .single();
      return !!data;
    } catch {
      return false;
    }
  },
}));
